/**
 * Asking a model with somebody's own key: which providers there are, what a
 * provider module has to do, and which model to preselect.
 *
 * **No model is named in this app.** Each provider's list comes from the key
 * itself (`listModels`), so there is no list here to keep current: a model the
 * provider releases appears, one it retires disappears, and what a model can
 * do (photos, how long a reply) is read from the provider's answer.
 *
 * The provider modules (`anthropicModel.ts`, `geminiModel.ts`) are imported on
 * first use, not with the app. The model chosen in Settings is remembered per
 * device and per provider in localStorage, beside the theme. Unlike the key it is not a
 * secret: it says which model, not who pays for it.
 */

export type ProviderId = "anthropic" | "google";

export interface ProviderInfo {
  /** Who the key is with, as the key form says it. */
  company: string;
  /** What the button asks. */
  assistant: string;
  /** A hint at what a key looks like, for the empty box. */
  keyHint: string;
  /** Where somebody manages that key, in words. */
  console: string;
}

export const PROVIDERS: Record<ProviderId, ProviderInfo> = {
  anthropic: {
    company: "Anthropic",
    assistant: "Claude",
    keyHint: "sk-ant-…",
    console: "your Anthropic console",
  },
  google: {
    company: "Google",
    assistant: "Gemini",
    keyHint: "AIza… or AQ.…",
    console: "Google AI Studio",
  },
};

export const PROVIDER_IDS = Object.keys(PROVIDERS) as ProviderId[];

export type Answer<T> = ({ ok: true } & T) | { ok: false; reason: string };

/** One model, as much of the provider's description as the screen and the request use. */
export interface ModelOption {
  id: string;
  /** The provider's own name for it, e.g. "Claude Opus 5.5". */
  name: string;
  /** The longest reply it can give, or null when the provider did not say. */
  maxTokens: number | null;
  /** Whether it can look at photos. */
  images: boolean;
}

/** One item's photos, ready to send. */
export interface ItemPhotos {
  itemId: string;
  images: { mediaType: "image/jpeg"; data: string }[];
}

/** What every provider module exports. */
export interface ProviderModule {
  /** Every model this key may use, best default first. Doubles as the check that the key works. */
  listModels(key: string, options?: { fetch?: typeof fetch }): Promise<Answer<{ models: ModelOption[] }>>;
  ask(
    prompt: string,
    key: string,
    model: ModelOption,
    options?: { photos?: readonly ItemPhotos[]; signal?: AbortSignal; fetch?: typeof fetch },
  ): Promise<Answer<{ text: string }>>;
}

export const loadProvider = (id: ProviderId): Promise<ProviderModule> =>
  id === "anthropic" ? import("./anthropicModel.js") : import("./geminiModel.js");

/** Room for ten items' JSON with thinking first, where the model allows that much. */
export const REPLY_TOKENS = 16000;

const MODEL_KEY = (provider: ProviderId) => `iltocat.enrichModel.${provider}`;

/**
 * The model chosen in Settings, whole: Fill in details asks with it without
 * listing models again. One retired since is answered by the provider with a
 * 404, which says to choose another.
 */
export function rememberedModel(provider: ProviderId): ModelOption | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(MODEL_KEY(provider)) ?? "null") as unknown;
    if (value && typeof value === "object" && typeof (value as ModelOption).id === "string") {
      const model = value as ModelOption;
      return {
        id: model.id,
        name: model.name || model.id,
        maxTokens: model.maxTokens ?? null,
        images: model.images !== false,
      };
    }
  } catch {
    // An id on its own, from before the whole model was kept: not enough to ask with.
  }
  return null;
}

export function rememberModel(provider: ProviderId, model: ModelOption): void {
  try {
    window.localStorage.setItem(MODEL_KEY(provider), JSON.stringify(model));
  } catch {
    // It still applies for this visit.
  }
}

const PHOTOS_KEY = "iltocat.enrichPhotos";

/** Whether photos go with the prompt. On unless turned off in Settings. */
export function sendPhotos(): boolean {
  try {
    return window.localStorage.getItem(PHOTOS_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSendPhotos(on: boolean): void {
  try {
    if (on) window.localStorage.removeItem(PHOTOS_KEY);
    else window.localStorage.setItem(PHOTOS_KEY, "off");
  } catch {
    // It still applies for this visit.
  }
}

/**
 * The model to preselect: the one chosen last time, if this key still has it,
 * else the first in the provider's order that can see photos, else the first.
 * A remembered model that has been retired falls through quietly, which is
 * the point.
 */
export function pickModel(models: readonly ModelOption[], remembered: string | null): ModelOption | null {
  return (
    models.find((model) => model.id === remembered) ??
    models.find((model) => model.images) ??
    models[0] ??
    null
  );
}
